import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { dateUtil } from "src/app/support/date-util";
import { first, isUndefined, omitBy, pickBy, random } from "lodash-es";
import { defer, forkJoin, iif, Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { Attachment } from "src/app/backend/models/attachment/attachment";
import { AttachmentCreate } from "src/app/backend/models/attachment/attachment.create";
import { StudyComment } from "src/app/backend/models/study-comment/study-comment";
import { StudyCommentCreate } from "src/app/backend/models/study-comment/study-comment.create";
import { StudyCommentDelete } from "src/app/backend/models/study-comment/study-comment.delete";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { UUID } from "src/app/support/uuid";
import { environment } from "src/environments/environment";
import { Study, StudyLight } from "../../models/study";
import Report from "../../models/report";
import { StudyStorage } from "../../models/study-storage";
import { StudyLoadTags } from "../../models/study/study.load-tags";
import { StudyUpdateTags } from "../../models/study/study.update-tags";
import { GroupBackend } from "src/app/backend/api/group.backend";
import {
  HTTP_CONTEXT_TOKEN_NO_CACHE,
  HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID
} from "src/app/core/http-context-tokens";
import { omitUndefinedAndNullProperties } from "src/app/support/object";
import { OptmisticSse } from "src/app/backend/decorators/optmistic-sse.decorator";
import { SseEvents } from "src/app/sse/sse-events";

type SendEmailParams = {
  email: string;
  report?: boolean;
  dicom?: boolean;
  notes?: string;
  attachments?: Array<{
    id: string;
    storageId: string;
    name?: string;
    description?: string;
    size?: number;
  }>;
};

type SendEmailsParams = Omit<SendEmailParams, "email"> & { emails: string[] };

@Injectable()
export class StudyBackend {
  private readonly groupBackend = inject(GroupBackend);
  private readonly http = inject(HttpClient);

  list(
    request: PageRequest & {
      availability: string | undefined;
      select?: string | undefined;
    }
  ): Observable<PaginatedResult<Study>> {
    return this.http
      .get<PaginatedResult<Study>>(
        `${environment.snoots.api.diagnostic}/studies`,
        {
          params: omitBy(
            {
              p: request.page,
              s: request.pageSize,
              ...request.filterValues,
              availability: request.availability,
              select: request.select
            },
            isUndefined
          )
        }
      )
      .pipe(
        switchMap((result) => {
          return iif(
            () => result.docs && result.docs.length > 0,
            defer(() =>
              forkJoin(
                result.docs.map((study) =>
                  iif(
                    () => !!study.assignee?.id,
                    defer(() =>
                      this.groupBackend
                        .loadLight(study.assignee!.id)
                        .pipe(catchError(() => of(undefined)))
                    ),
                    of(undefined)
                  ).pipe(
                    map((group) => ({
                      ...study,
                      group
                    }))
                  )
                )
              ).pipe(map((studies) => ({ ...result, docs: studies })))
            ),
            of(result)
          );
        })
      );
  }

  importFromStorage({
    version,
    body
  }: {
    version: "v1";
    body: {
      storage: {
        id: string;
        name: string;
      };
      accessionNumber?: string;
      stationName?: string;
      modality?: string;
      seriesDescription?: string;
      studyDescription?: string;
      acquisitionDate?: string;
      ownerNominative?: string;
      patientId?: string;
      patientName?: string;
      patientSpeciesDescription?: string;
      patientBreedDescription?: string;
      note?: string;
    };
  }): Observable<void> {
    switch (version) {
      case "v1": {
        return this.http.post<void>(
          `${environment.snoots.api.diagnostic}/v1/imports`,
          body,
          {
            context: new HttpContext().set(
              HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
              true
            )
          }
        );
      }
    }
  }

  loadStudyById(filterId: string): Observable<Study> {
    return this.http
      .get<Study>(`${environment.snoots.api.diagnostic}/studies/${filterId}`, {
        headers: {
          "snt-product": "rocket"
        }
      })
      .pipe(
        switchMap((study: Study) =>
          iif(
            () => !!study.assignee?.id,
            defer(() =>
              this.groupBackend
                .loadLight(study.assignee!.id)
                .pipe(catchError(() => of(undefined)))
            ),
            of(undefined)
          ).pipe(
            map((group) => ({
              ...study,
              group
            }))
          )
        )
      );
  }

  loadStudyByIdLight(filterId: string): Observable<StudyLight> {
    return this.http.get<StudyLight>(
      `${environment.snoots.api.diagnostic}/studies/${filterId}`,
      {
        params: {
          light: true
        },
        headers: {
          "snt-product": "rocket"
        }
      }
    );
  }

  loadStudyByUuid(uuid: string): Observable<Study | undefined> {
    return this.http
      .get<PaginatedResult<Study>>(
        `${environment.snoots.api.diagnostic}/studies`,
        {
          params: {
            studyInstanceUID: uuid
          }
        }
      )
      .pipe(
        map((result) => {
          return first(result.docs);
        })
      );
  }

  loadStudyByIdNoCache(id: string): Observable<Study> {
    return this.http.get<Study>(
      `${environment.snoots.api.diagnostic}/studies/${id}`,
      {
        headers: {
          "snt-product": "rocket"
        },
        context: new HttpContext().set(HTTP_CONTEXT_TOKEN_NO_CACHE, true)
      }
    );
  }

  loadStudyByIdForLanding({
    id,
    token
  }: {
    id: string;
    token: string;
  }): Observable<Study> {
    return this.http.get<Study>(
      `${environment.snoots.api.diagnostic}/studies/${id}`,
      {
        headers: {
          member: `Bearer ${token}`
        }
      }
    );
  }

  loadStudyByRequest(
    filterId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Study>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies`,
      {
        params: <any>pickBy({
          p: request.page,
          s: request.pageSize,
          requestId: filterId,
          ...request.filterValues
        })
      }
    );

    return studies;
  }

  loadStudyLightByRequest(
    filterId: string,
    request: PageRequest
  ): Observable<PaginatedResult<StudyLight>> {
    const studies = this.http.get<PaginatedResult<StudyLight>>(
      `${environment.snoots.api.diagnostic}/studies`,
      {
        params: <any>pickBy({
          p: request.page,
          s: request.pageSize,
          requestId: filterId,
          light: true,
          ...request.filterValues
        })
      }
    );

    return studies;
  }

  loadStudyByPatient(
    patientId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Study>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies`,
      {
        params: <any>pickBy({
          p: request.page,
          s: request.pageSize,
          patientId: patientId,
          ...request.filterValues
        })
      }
    );

    return studies;
  }

  loadStudyByCustomer(
    customerId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Study>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies`,
      {
        params: <any>pickBy({
          p: request.page,
          s: request.pageSize,
          customerId: customerId,
          ...request.filterValues
        })
      }
    );

    return studies;
  }

  loadStudyByDevice(
    deviceId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Study>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies`,
      {
        params: <any>pickBy({
          p: request.page,
          s: request.pageSize,
          ...request.filterValues,
          deviceId: deviceId
        })
      }
    );

    return studies;
  }

  loadStudyStorage(
    storageId: string,
    request: PageRequest
  ): Observable<PaginatedResult<StudyStorage>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies`,
      {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize
          },
          isUndefined
        )
      }
    );

    return studies.pipe(
      map((result) => {
        return {
          ...result,
          docs: result.docs.map(
            (x) =>
              <StudyStorage>{
                id: x.id,
                number: x.number,
                createdAt: x.createdAt,
                customerLastName: x.customer?.lastName || "",
                customerFirstName: x.customer?.firstName || "",
                customerUuid: x.customer?.id,
                patientName: x.patient?.name,
                patientUuid: x.patient?.id,
                size: random(1024 * 1024 * 512, 5 * 1024 * 1024 * 1024),
                expiresAt: dateUtil().add(random(5, 45), "day").toISOString(),
                updatedAt: dateUtil().add(random(-5, -45), "day").toISOString()
              }
          )
        };
      })
    );
  }

  loadStudyCompleted(request: PageRequest): Observable<PaginatedResult<Study>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies?status=assigned`,
      {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues
          },
          isUndefined
        )
      }
    );

    return studies;
  }

  loadStudyByReportAssigned(
    request: PageRequest,
    reporterId: string
  ): Observable<PaginatedResult<Study>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies?status=assigned`,
      {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            assigneeId: reporterId,
            ...request.filterValues
          },
          isUndefined
        )
      }
    );

    return studies;
  }

  loadStudyByReportAssignedAndReporting(
    request: PageRequest,
    reporterId: string,
    params?: {
      status?: string[];
    }
  ): Observable<PaginatedResult<Study>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies`,
      {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            assigneeId: reporterId,
            status: params?.status?.join(","),
            ...request.filterValues
          },
          isUndefined
        )
      }
    );

    return studies;
  }

  loadStudyByReportReported(
    request: PageRequest,
    reporterId: string
  ): Observable<PaginatedResult<Study>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies?status=reported`,
      {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            assigneeId: reporterId,
            ...request.filterValues
          },
          isUndefined
        )
      }
    );

    return studies;
  }

  loadStudyReported(request: PageRequest): Observable<PaginatedResult<Study>> {
    const studies = this.http.get<PaginatedResult<Study>>(
      `${environment.snoots.api.diagnostic}/studies?status=reported`,
      {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize
          },
          isUndefined
        )
      }
    );

    return studies;
  }

  download(id: string): Observable<Blob> {
    let url = `${environment.snoots.api.diagnostic}/studies/${id}/download`;
    return this.http.get(url, { responseType: "blob" });
  }

  @UserNotify({
    successMessage: marker("Studio cancellato"),
    errorMessage: marker("Non è stato possibile cancellare lo studio")
  })
  delete(studyId: string): Observable<string> {
    return this.http.delete<string>(
      `${environment.snoots.api.diagnostic}/studies/${studyId}`
    );
  }

  @UserNotify({
    successMessage: marker("Studio aggiornato"),
    errorMessage: marker("Non è stato possibile aggiornare lo studio")
  })
  update(data: any, id: string): Observable<Study> {
    return this.http.patch<Study>(
      `${environment.snoots.api.diagnostic}/studies/${id}`,
      data
    );
  }

  @UserNotify({
    successMessage: marker("Studi esportati in Excel"),
    errorMessage: marker("Impossibile esportare i studi"),
    progressIndeterminate: true,
    progressMessage: marker("Esportazione studi in corso")
  })
  exportMassiveExcel(studyIds: string[]): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.diagnostic}/studies/exports`,
      { studyIds: studyIds }
    );
  }

  @UserNotify({
    successMessage: marker("Referto annullato"),
    errorMessage: marker("Non è stato possibile annullare il referto")
  })
  invalidateReport(studyId: string, attachmentId: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.diagnostic}/studies/${studyId}/reports/${attachmentId}/reject`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Studio assegnato"),
    errorMessage: marker("Non è stato possibile assegnare lo studio")
  })
  assign(
    studyId: string,
    assignee: {
      id: string;
      type: "group" | "member";
    }
  ): Observable<string> {
    return this.http.put<string>(
      `${environment.snoots.api.diagnostic}/studies/${studyId}/assign`,
      {
        assignee
      },
      {
        context: new HttpContext().set(
          HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
          true
        )
      }
    );
  }

  createAttachment(
    studyId: string,
    data: AttachmentCreate
  ): Observable<Attachment> {
    return this.http.post<Attachment>(
      `${environment.snoots.api.diagnostic}/studies/${studyId}/attachments`,
      data
    );
  }

  deleteAttachment(attachmentId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.diagnostic}/attachments/${attachmentId}`
    );
  }

  loadAttachments(studyId: string): Observable<Attachment[]> {
    return this.http.get<Attachment[]>(
      `${environment.snoots.api.diagnostic}/studies/${studyId}/attachments`
    );
  }

  loadAttachmentsForLanding({
    studyId,
    token
  }: {
    studyId: string;
    token: string;
  }): Observable<Attachment[]> {
    return this.http.get<Attachment[]>(
      `${environment.snoots.api.diagnostic}/studies/${studyId}/attachments`,
      {
        headers: {
          member: `Bearer ${token}`
        }
      }
    );
  }

  loadAttachmentsWithoutReports(studyId: string): Observable<Attachment[]> {
    return this.loadAttachments(studyId).pipe(
      map((attachments) => attachments.filter((x) => x.type !== "report"))
    );
  }

  loadReports(studyId: string): Observable<Report[]> {
    return this.http.get<Report[]>(
      `${environment.snoots.api.diagnostic}/studies/${studyId}/reports`
    );
  }

  loadComments(): Observable<StudyComment[]> {
    return of([]);
  }

  loadCommentsByStudy({
    studyUuid
  }: {
    studyUuid: UUID;
  }): Observable<StudyComment[]> {
    return of([]);
  }

  @UserNotify({
    successMessage: marker("Messaggio creato"),
    errorMessage: marker("Non è stato possibile creare il messaggio")
  })
  createComment(request: StudyCommentCreate): Observable<void> {
    return of(undefined);
  }

  @UserNotify({
    successMessage: marker("Messaggio rimosso"),
    errorMessage: marker("Non è stato possibile rimuovere il messaggio")
  })
  deleteComment(request: StudyCommentDelete): Observable<void> {
    return of(undefined);
  }

  @UserNotify({
    successMessage: marker("Email inviata"),
    errorMessage: marker("Non è stato possibile l'email")
  })
  sendMultipleEmail(
    id: string,
    sendEmailsParams: SendEmailsParams
  ): Observable<void[]> {
    const { emails, ...rest } = sendEmailsParams;

    return forkJoin(
      emails.map((email: string) => {
        return this.http.put<void>(
          `${environment.snoots.api.diagnostic}/studies/${id}/deliver`,
          omitUndefinedAndNullProperties({
            email,
            report: rest.report,
            dicom: rest.dicom,
            notes: rest.notes,
            attachments: rest.attachments
          }),
          {
            context: new HttpContext().set(
              HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
              true
            )
          }
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Tag aggiornati"),
    errorMessage: marker("Non è stato possibile aggiornare i tag")
  })
  updateTags(id: string, request: StudyUpdateTags): Observable<Study> {
    return this.http.patch<Study>(
      `${environment.snoots.api.diagnostic}/studies/${id}`,
      request
    );
  }

  loadTags(request: StudyLoadTags): Observable<string[]> {
    const customer = this.http.get<Study>(
      `${environment.snoots.api.diagnostic}/studies/${request.id}`
    );

    return customer.pipe(map((study) => study.tags || []));
  }

  @UserNotify({
    successMessage: marker("Emoji aggiornate"),
    errorMessage: marker("Non è stato possibile aggiornare le emoji")
  })
  updateEmoji(id: string, emoji: string): Observable<void> {
    return of(undefined);
  }

  loadEmoji(id: string): Observable<string> {
    return of("");
  }

  updateReport(id: string, data: any): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.diagnostic}/studies/${id}/report`,
      data
    );
  }

  @UserNotify({
    successMessage: marker("Referto creato"),
    errorMessage: marker("Impossibile creare il referto")
  })
  createReport(id: string, data: any): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.diagnostic}/studies/${id}/report`,
      data
    );
  }

  completeReport(
    id: string,
    data: { number: string; attachmentId: string }
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.diagnostic}/studies/${id}/report/complete`,
      data
    );
  }

  deleteReport(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.diagnostic}/studies/${id}/report`
    );
  }

  @UserNotify({
    successMessage: marker("Studio rifiutato"),
    errorMessage: marker("Non è stato possibile rifiutare lo studio")
  })
  reject(id: string, note: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.diagnostic}/studies/${id}/reject`,
      {
        note: note
      }
    );
  }

  @UserNotify({
    successMessage: marker("Referto annullato"),
    errorMessage: marker("Non è stato possibile annullare il referto")
  })
  resetTemplate(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.diagnostic}/studies/${id}/report`
    );
  }

  @UserNotify({
    // successMessage: marker('Worklist creata'),
    errorMessage: marker("Non è stato possibile creare la worklist")
  })
  createWorklist(data: string): Observable<any> {
    return this.http.post<any>(
      `${environment.snoots.api.diagnostic}/studies/${data}/worklists`,
      {}
    );
  }

  getReportUrl(id: string): Observable<string> {
    return this.http
      .get<{
        url: string;
      }>(
        `${environment.snoots.api.diagnostic}/studies/${id}/report/download-url`
      )
      .pipe(
        map((response) => {
          return response.url;
        })
      );
  }

  getReportUrlForLanding({
    id,
    token
  }: {
    id: string;
    token: string;
  }): Observable<string> {
    return this.http
      .get<{
        url: string;
      }>(
        `${environment.snoots.api.diagnostic}/studies/${id}/report/download-url`,
        {
          headers: {
            member: `Bearer ${token}`
          }
        }
      )
      .pipe(
        map((response) => {
          return response.url;
        })
      );
  }

  @UserNotify({
    successMessage: marker("Studio condiviso"),
    errorMessage: marker("Non è stato possibile condividere lo studio")
  })
  share(data: string): Observable<any> {
    return this.http.put<any>(
      `${environment.snoots.api.diagnostic}/studies/${data}/share-new`,
      {}
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.DIAGNOSTIC_STUDY_UPDATED,
        payloadFactory: ({ args }: { args?: any[] }) => {
          return {
            id: args![0],
            archivingRequestedAt: new Date().toISOString(),
            archive: {
              storageId: args![0]
            }
          };
        }
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Studio archiviato"),
    errorMessage: marker("Non è stato possibile archiviare lo studio")
  })
  archive(data: string): Observable<any> {
    return this.http.put<any>(
      `${environment.snoots.api.diagnostic}/studies/${data}/archive`,
      {}
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.DIAGNOSTIC_STUDY_UPDATED,
        payloadFactory: ({ args }: { args?: any[] }) => {
          return {
            id: args![0],
            archive: {
              restorationRequestedAt: new Date().toISOString()
            }
          };
        }
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Studio ripristinato"),
    errorMessage: marker("Non è stato possibile ripristinare lo studio")
  })
  restore(data: string): Observable<any> {
    return this.http.put<any>(
      `${environment.snoots.api.diagnostic}/studies/${data}/restore`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Studio archiviato"),
    errorMessage: marker("Non è stato possibile archiviare lo studio")
  })
  archiveBulk(studyIds: string[]): Observable<any> {
    return this.http.post<any>(
      `${environment.snoots.api.diagnostic}/studies/archive`,
      {
        studyIds: studyIds
      }
    );
  }

  @UserNotify({
    successMessage: marker("Studio ripristinato"),
    errorMessage: marker("Non è stato possibile ripristinare lo studio")
  })
  restoreBulk(studyIds: string[]): Observable<any> {
    return this.http.post<any>(
      `${environment.snoots.api.diagnostic}/studies/restore`,
      {
        studyIds: studyIds
      }
    );
  }
}
